import { useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $getRoot, $createParagraphNode, $createTextNode, EditorState } from 'lexical';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  videoUrl: string;
  image: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  featured: boolean;
}

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    videoUrl: '',
    imagePrompt: '',
    featured: false
  });

  const CORRECT_PASSCODE = '624907';

  // Lexical editor configuration
  const editorConfig = {
    namespace: 'BlogEditor',
    theme: {
      paragraph: 'mb-2',
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
      heading: {
        h1: 'text-3xl font-bold mb-4',
        h2: 'text-2xl font-bold mb-3',
        h3: 'text-xl font-bold mb-2',
      },
      list: {
        ul: 'list-disc ml-6 mb-2',
        ol: 'list-decimal ml-6 mb-2',
        listitem: 'mb-1',
      },
      link: 'text-[#D4A574] underline hover:text-[#2C1810]',
    },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    onError: (error: Error) => {
      console.error(error);
    },
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === CORRECT_PASSCODE) {
      setIsAuthenticated(true);
    } else {
      alert('Incorrect passcode. The passcode is 624907');
    }
  };

  const handleEditorChange = (editorState: EditorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      setFormData({...formData, content: textContent});
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newPost: BlogPost = {
      id: Date.now().toString(),
      title: formData.title,
      excerpt: formData.excerpt,
      content: formData.content,
      videoUrl: formData.videoUrl,
      image: formData.imagePrompt 
        ? `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28formData.imagePrompt%29%7D&width=800&height=600&seq=blog-${Date.now()}&orientation=landscape`
        : 'https://readdy.ai/api/search-image?query=elegant%20minimalist%20blog%20post%20header%20warm%20earth%20tones%20beige%20brown%20gold%20clean%20geometric%20retro%20aesthetic&width=800&height=600&seq=blog-default&orientation=landscape',
      author: 'E&I Legacy Holdings',
      date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      readTime: `${Math.ceil(formData.content.split(' ').length / 200)} min read`,
      category: 'Blog',
      featured: formData.featured
    };

    setPosts([newPost, ...posts]);
    setShowForm(false);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      videoUrl: '',
      imagePrompt: '',
      featured: false
    });
    
    alert('Post created successfully! Note: This is temporary storage. Connect to Supabase to save permanently.');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      setPosts(posts.filter(post => post.id !== id));
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-[#F5E6D3] flex items-center justify-center px-6 pt-20">
          <div className="max-w-md w-full">
            <div className="bg-white border-4 border-[#2C1810] p-8">
              <h1 className="text-3xl font-bold text-[#2C1810] mb-6 font-['Space_Mono']">
                🔒 ADMIN LOGIN
              </h1>
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-['Space_Mono'] text-[#2C1810] mb-2">
                    PASSCODE
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
                >
                  LOGIN
                </button>
              </form>
              <p className="mt-6 text-xs text-[#5C4033] font-['DM_Sans']">
                Note: Connect to Supabase for secure authentication
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5E6D3]">
      <Navigation />
      
      <div className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center mb-12">
            <div>
              <div className="w-16 h-1 bg-[#D4A574] mb-4"></div>
              <h1 className="text-5xl font-bold text-[#2C1810] font-['Space_Mono']">
                BLOG MANAGEMENT
              </h1>
              <div className="flex items-center gap-3 mt-4">
                <span className="text-sm font-['Space_Mono'] text-[#5C4033]">
                  Status:
                </span>
                <span className="flex items-center gap-2 text-sm font-['Space_Mono'] text-[#2C1810]">
                  <i className="ri-lock-unlock-line text-lg"></i>
                  Authorized
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-8 py-3 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
            >
              {showForm ? 'CANCEL' : 'CREATE NEW POST'}
            </button>
          </div>

          {showForm && (
            <div className="bg-white border-4 border-[#2C1810] p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#2C1810] mb-6 font-['Space_Mono']">
                CREATE NEW POST
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-['Space_Mono'] text-[#2C1810] mb-2">
                    TITLE *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-['Space_Mono'] text-[#2C1810] mb-2">
                    EXCERPT (Short Description) *
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm h-24 resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-['Space_Mono'] text-[#2C1810] mb-2">
                    CONTENT * (Rich Text Editor)
                  </label>
                  <div className="border-2 border-[#2C1810] focus-within:border-[#D4A574] transition-colors bg-white">
                    <LexicalComposer initialConfig={editorConfig}>
                      <div className="relative">
                        <RichTextPlugin
                          contentEditable={
                            <ContentEditable className="min-h-[300px] p-4 outline-none font-['DM_Sans'] text-sm text-[#2C1810]" />
                          }
                          placeholder={
                            <div className="absolute top-4 left-4 text-[#5C4033]/50 pointer-events-none font-['DM_Sans'] text-sm">
                              Write your blog content here...
                            </div>
                          }
                          ErrorBoundary={LexicalErrorBoundary}
                        />
                        <HistoryPlugin />
                        <OnChangePlugin onChange={handleEditorChange} />
                      </div>
                    </LexicalComposer>
                  </div>
                  <p className="text-xs text-[#5C4033] mt-2 font-['DM_Sans']">
                    Type your content above. The editor supports rich text formatting.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-['Space_Mono'] text-[#2C1810] mb-2">
                    VIDEO URL (YouTube, Vimeo, etc.)
                  </label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                    placeholder="https://www.youtube.com/embed/..."
                    className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                  />
                  <p className="text-xs text-[#5C4033] mt-1 font-['DM_Sans']">
                    Use embed URL format for videos
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-['Space_Mono'] text-[#2C1810] mb-2">
                    IMAGE DESCRIPTION (AI will generate)
                  </label>
                  <input
                    type="text"
                    value={formData.imagePrompt}
                    onChange={(e) => setFormData({...formData, imagePrompt: e.target.value})}
                    placeholder="Describe the image you want..."
                    className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                    className="w-5 h-5 border-2 border-[#2C1810] cursor-pointer"
                  />
                  <label htmlFor="featured" className="text-sm font-['Space_Mono'] text-[#2C1810] cursor-pointer">
                    FEATURE THIS POST
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
                >
                  PUBLISH POST
                </button>
              </form>
            </div>
          )}

          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#2C1810] font-['Space_Mono']">
              YOUR POSTS ({posts.length})
            </h2>
            
            {posts.length === 0 ? (
              <div className="bg-white border-4 border-[#2C1810] p-12 text-center">
                <p className="text-[#5C4033] font-['DM_Sans']">
                  No posts yet. Create your first post to get started!
                </p>
              </div>
            ) : (
              <div className="grid gap-6">
                {posts.map((post) => (
                  <div key={post.id} className="bg-white border-4 border-[#2C1810] p-6">
                    <div className="flex justify-between items-start gap-6">
                      <div className="flex-1">
                        {post.featured && (
                          <span className="inline-block px-3 py-1 bg-[#D4A574] text-[#2C1810] text-xs font-['Space_Mono'] mb-3">
                            FEATURED
                          </span>
                        )}
                        <h3 className="text-2xl font-bold text-[#2C1810] mb-2 font-['Space_Mono']">
                          {post.title}
                        </h3>
                        <p className="text-[#5C4033] mb-4 font-['DM_Sans'] text-sm">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-[#D4A574] font-['Space_Mono']">
                          <span>{post.date}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                          {post.videoUrl && (
                            <>
                              <span>•</span>
                              <span>📹 VIDEO</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="px-6 py-2 border-2 border-[#2C1810] text-[#2C1810] font-['Space_Mono'] text-sm hover:bg-[#2C1810] hover:text-[#F5E6D3] transition-all cursor-pointer whitespace-nowrap"
                        >
                          DELETE
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-12 bg-[#D4A574]/20 border-2 border-[#D4A574] p-6">
            <h3 className="text-lg font-bold text-[#2C1810] mb-2 font-['Space_Mono']">
              ⚠️ IMPORTANT NOTE
            </h3>
            <p className="text-sm text-[#5C4033] font-['DM_Sans']">
              Currently, posts are stored temporarily in your browser. To save posts permanently and enable secure authentication, you need to connect to Supabase. This will allow you to manage your blog from any device and keep your content safe.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
